/**
 * OPUS-1045 — calendar date filter for the fixture picker.
 *
 * A compact, dependency-free month calendar. Days that have at least one
 * fixture are marked with a coral dot and are the only selectable cells
 * (selecting an empty day would just filter to nothing). Clicking the
 * selected day again clears the filter ("All dates").
 *
 * Pure presentational + local view-month state; the parent owns the
 * selected date and the marked-date set.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (n) => String(n).padStart(2, "0");
const isoOf = (y, m /* 0-11 */, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

export default function FixtureCalendar({ markedDates = [], value = "", onChange }) {
  const markedSet = useMemo(() => new Set(markedDates), [markedDates]);
  // Earliest marked day — a stable primitive so the snap effect only fires
  // when the underlying fixture set actually changes (not every render).
  const earliest = useMemo(
    () => (markedDates.length ? [...markedDates].sort()[0] : ""),
    [markedDates],
  );

  // The month currently on screen. Init from the selection, else the first
  // fixture month, else today.
  const initial = () => {
    const anchor = value || earliest;
    const d = anchor ? new Date(`${anchor}T00:00:00`) : new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  };
  const [view, setView] = useState(initial);

  // Snap to the selected date / first fixture month when those change.
  useEffect(() => {
    const anchor = value || earliest;
    if (!anchor) return;
    const d = new Date(`${anchor}T00:00:00`);
    setView({ y: d.getFullYear(), m: d.getMonth() });
  }, [value, earliest]);

  const { y, m } = view;
  const firstWeekday = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const step = (delta) => {
    const next = new Date(y, m + delta, 1);
    setView({ y: next.getFullYear(), m: next.getMonth() });
  };

  const cellBtn = {
    width: 30, height: 30, borderRadius: "8px", border: "1px solid transparent",
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative", fontSize: "0.8rem", lineHeight: 1, userSelect: "none",
  };

  return (
    <Box
      sx={{
        border: "1px solid var(--border-strong)", borderRadius: "10px",
        backgroundColor: "var(--elevated)", p: 1, width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <IconButton size="small" onClick={() => step(-1)} sx={{ color: "var(--text-muted)" }} aria-label="Previous month">
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--text)" }}>
          {MONTHS[m]} {y}
        </Typography>
        <IconButton size="small" onClick={() => step(1)} sx={{ color: "var(--text-muted)" }} aria-label="Next month">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", justifyItems: "center" }}>
        {WEEKDAYS.map((w) => (
          <Typography key={w} sx={{ fontSize: "0.65rem", color: "var(--text-dim)", textTransform: "uppercase" }}>{w}</Typography>
        ))}
        {cells.map((d, i) => {
          if (d == null) return <Box key={`x${i}`} sx={cellBtn} />;
          const iso = isoOf(y, m, d);
          const marked = markedSet.has(iso);
          const selected = value === iso;
          return (
            <Box
              key={iso}
              onClick={marked ? () => onChange?.(selected ? "" : iso) : undefined}
              sx={{
                ...cellBtn,
                cursor: marked ? "pointer" : "default",
                color: selected ? "#000" : marked ? "var(--text)" : "var(--text-dim)",
                opacity: marked ? 1 : 0.4,
                backgroundColor: selected ? "var(--coral)" : "transparent",
                borderColor: selected ? "var(--coral)" : "transparent",
                "&:hover": marked && !selected ? { borderColor: "var(--coral)" } : undefined,
              }}
            >
              {d}
              {marked && !selected && (
                <Box sx={{
                  position: "absolute", bottom: 3, left: "50%", transform: "translateX(-50%)",
                  width: 4, height: 4, borderRadius: "50%", backgroundColor: "var(--coral)",
                }} />
              )}
            </Box>
          );
        })}
      </Box>

      {value && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
          <Typography
            onClick={() => onChange?.("")}
            sx={{ fontSize: "0.7rem", color: "var(--text-muted)", cursor: "pointer", "&:hover": { color: "var(--coral)" } }}
          >
            Clear · show all dates
          </Typography>
        </Box>
      )}
    </Box>
  );
}
